import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, map, of, throwError } from "rxjs";
import { PremiseHazardRestBase } from "@rest/premise-hazard-rest-base";
import { ResponseItem } from "@models/response-item.model";
import { Units } from "@models/units.model";
import { EnvService } from "@services/env.service";
import { ToastServiceManager } from "@services/toast-service-manager.service";
import { UserService } from "@services/user.service";
import { CurrentUnitsService } from "@services/current-units.service";
import { TranslocoService } from "@jsverse/transloco";
import { TemplateConstants } from "../../constants/template-constants";
import { PremiseHazardReportRequestModel } from "@app/models/premise-hazard/request/premise-hazard-report-request.model";

@Injectable({
  providedIn: 'root',
})

export class AdminRestService extends PremiseHazardRestBase {

  constructor(
    httpClient: HttpClient,
    envService: EnvService,
    toastServiceManager: ToastServiceManager,
    userService: UserService,
    private readonly currentUnitsService: CurrentUnitsService,
    private readonly transloco: TranslocoService,
  ) {
    super(httpClient, envService, toastServiceManager, userService);
  }

  public initializeCustomer(): Observable<void> {
    const opts = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
    };

    const url = `${this.phzBaseUrl}${this.adminUrl}/init${this.addCustomerRoute()}`;

    return this.httpClient.post<void>(url, null, opts)
      .pipe(catchError(err => {
        if (err instanceof HttpErrorResponse) {
          const httpError = err as HttpErrorResponse;
          this.handleHttpError(httpError);
        }
        return throwError(() => err);
      }));
  }

  public getSharingCustomers(): Observable<ResponseItem<Array<string>>> {
    const opts = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      observe: 'response' as 'response',
    };

    const url = `${this.phzBaseUrl}${this.adminUrl}${this.addCustomerRoute()}/sharing`;

    return this.httpClient.get<Array<string>>(url, opts)
      .pipe(catchError(err => {
        if (err instanceof HttpErrorResponse) {
          const httpError = err as HttpErrorResponse;
          this.handleHttpError(httpError);
        }
        return throwError(() => err);
      }),
        map(response => {
          const result: ResponseItem<Array<string>> = {
            Item: response.body!,
            ETag: response.headers.get('ETag')!,
          };
          return result;
        }));
  }

  public updateSharingCustomers(customers: Array<string>, etag: string): Observable<ResponseItem<void>> {
    const opts = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
        'If-Match': etag,
      }),
      observe: 'response' as 'response',
    };
    const url = `${this.phzBaseUrl}${this.adminUrl}${this.addCustomerRoute()}/sharing`;
    return this.httpClient.put<void>(url, customers, opts)
      .pipe(catchError(err => {
        if (err instanceof HttpErrorResponse) {
          const httpError = err as HttpErrorResponse;
          this.handleHttpError(httpError);
        }
        return throwError(() => err);
      }),
        map(response => {
          const result: ResponseItem<void> = {
            Item: undefined,
            ETag: response.headers.get('ETag')!,
          };
          return result;
        }));
  }

  public getCustomersConfiguration(): Observable<ResponseItem<boolean>> {
    const opts = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      observe: 'response' as 'response',
    };

    const url = `${this.phzBaseUrl}${this.adminUrl}${this.addCustomerRoute()}/configuration`;

    return this.httpClient.get<boolean>(url, opts)
      .pipe(catchError(err => {
        if (err instanceof HttpErrorResponse) {
          const httpError = err as HttpErrorResponse;
          this.handleHttpError(httpError);
        }
        return throwError(() => err);
      }),
        map(response => {
          const result: ResponseItem<boolean> = {
            Item: response.body!,
            ETag: response.headers.get('ETag')!,
          };
          return result;
        }));
  }

  public getDefaultUnits(): Observable<Units> {
    const opts = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      observe: 'response' as 'response',
    };

    const url = `${this.phzBaseUrl}${this.adminUrl}${this.addCustomerRoute()}/defaultUnits`;

    return this.httpClient.get<Units>(url, opts)
      .pipe(catchError(err => {
        if (err instanceof HttpErrorResponse) {
          const httpError = err as HttpErrorResponse;
          if (err.status !== 404) {
            this.handleHttpError(httpError);
          } else {
            const currentLang = this.transloco.getActiveLang();
            let units: Units = Units.Meter;
            if (currentLang === 'en') {
              units = Units.Foot;
            }
            this.currentUnitsService.setCurrentUnits(units);
            return of(units);
          }
        }
        return throwError(() => err);
      }),
        map(response => {
          if (typeof response === 'number') {
            const unitsResponse: Units = response as Units;
            this.currentUnitsService.setCurrentUnits(unitsResponse);
            return unitsResponse;
          }

          const httpResponse = response as HttpResponse<Units>;
          const result: Units = httpResponse.body! as Units;
          this.currentUnitsService.setCurrentUnits(result);
          return result;
        }));
  }

  public updateDefaultUnits(units: Units): Observable<void> {
    const opts = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      observe: 'response' as 'response',
    };
    const url = `${this.phzBaseUrl}${this.adminUrl}${this.addCustomerRoute()}/defaultUnits`;
    return this.httpClient.put<void>(url, units, opts)
      .pipe(catchError(err => {
        if (err instanceof HttpErrorResponse) {
          const httpError = err as HttpErrorResponse;
          this.handleHttpError(httpError);
        }
        return throwError(() => err);
      }),
        map(response => {
          this.currentUnitsService.setCurrentUnits(units);
          return;
        }));
  }

  public getUserPermissions(): Observable<Array<string>> {
    const opts = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      observe: 'response' as 'response',
    };

    const url = `${this.phzBaseUrl}${this.adminUrl}${this.addCustomerRoute()}/permissions`;

    return this.httpClient.get<Array<string>>(url, opts)
      .pipe(catchError(err => {
        if (err instanceof HttpErrorResponse) {
          const httpError = err as HttpErrorResponse;
          this.handleHttpError(httpError);
        }
        return throwError(() => err);
      }),
        map(response => {
          const result: Array<string> = response.body!;
          return result;
        }));
  }


  public createHazardReport(requestModel: PremiseHazardReportRequestModel): Observable<Blob> {
    const url = `${this.phzBaseUrl}${this.reportUrl}${this.addCustomerRoute()}/`;

    return this.httpClient.post(url, requestModel, {
            responseType: 'blob',
            headers: new HttpHeaders({
              'MsiProduct': this.MsiProduct,
              'Content-Type': 'application/json',
              'Accept': 'application/zip'
            }),
          })
           .pipe(catchError(err => {
              if (err instanceof HttpErrorResponse) {
                const httpError = err as HttpErrorResponse;
                this.handleHttpError(httpError);
              }
              return throwError(() => err);
            }));
  }
}
